import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
  FaBars,
  FaTimes,
} from "react-icons/fa";
import "./HeaderComponent.css";
import logo from "../../assets/credtech_logo.jpg";
import defaultCityImage from "../../assets/cityimag.png.png";
import { EventService } from "../../core/service/EventService";
import {
  formatEventDisplayDate,
  getEventSortDate,
  isEventPast,
  isEventWithinMonths,
  resolveEventRange,
} from "../../utils/eventDateUtils";

// City image mapping - normalized keys
const cityImageMap = {
  pune: require("../../assets/puneImg.png"),
  chennai: require("../../assets/CheImg.png"),
  bangalore: require("../../assets/BangImg.png"), // handle API spelling
  banglore: require("../../assets/BangImg.png"), // alternative spelling
  hyderabad: require("../../assets/HydImg.png"),
  // add more cities as needed
};

// Normalize city name for matching
const normalizeCityName = (cityName) => {
  if (!cityName) return "";
  return cityName.toLowerCase().replace(/\s+/g, "");
};

const HeaderComponent = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [latestEvent, setLatestEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRegisterEnabled, setIsRegisterEnabled] = useState(false);

  useEffect(() => {
    const fetchLatestEvent = async () => {
      try {
        setLoading(true);
        const eventService = new EventService();
        const response = await eventService.getAllEvents();

        if (
          response &&
          response.data &&
          response.data.responseBody &&
          Array.isArray(response.data.responseBody) &&
          response.data.responseBody.length > 0
        ) {
          const futureEvents = response.data.responseBody
            .filter((event) => !isEventPast(event))
            .sort((a, b) => getEventSortDate(a) - getEventSortDate(b));

          let latest;
          if (futureEvents.length > 0) {
            latest = futureEvents[0];
          } else {
            const pastEvents = [...response.data.responseBody].sort(
              (a, b) => getEventSortDate(b) - getEventSortDate(a),
            );
            latest = pastEvents[0];
          }

          setLatestEvent(latest);
          setIsRegisterEnabled(latest ? isEventWithinMonths(latest, 3) : false);
        } else {
          setError("No upcoming events found");
        }
      } catch (err) {
        setError("Failed to load event information");
        console.error("Error fetching events:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestEvent();
  }, []);

  const getCityImage = (cityName) => {
    const normalized = normalizeCityName(cityName);
    return cityImageMap[normalized] || defaultCityImage;
  };

  const handleRegisterClick = () => {
    if (latestEvent) {
      navigate("/register-now", {
        state: {
          city: latestEvent.city,
          date: formatEventDisplayDate(latestEvent),
          image: getCityImage(latestEvent.city),
          euid: latestEvent.euid || "",
        },
      });
    } else {
      navigate("/register-now");
    }
  };

  const navItems = [
    { name: "About us", path: "/aboutus" },
    { name: "Upcoming Platforms", path: "/platforms" },
    { name: "Gallery", path: "/gallery" },
    { name: "Contact Us", path: "/contact" },
  ];

  const compactHeaderDate = () => {
    if (!latestEvent) return "Jan 1";
    const { start, end } = resolveEventRange(latestEvent);
    if (!start) return formatEventDisplayDate(latestEvent);

    const sameDay =
      end &&
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate();

    if (sameDay || !end) {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return `${months[start.getMonth()]} ${start.getDate()}`;
    }

    return formatEventDisplayDate(latestEvent);
  };

  return (
    <header className="header-container">
      <div className="header-wrapper">
        <div className="top-bar d-flex justify-content-between align-items-center">
          <div className="logo-container">
            <Link to="/">
              <img
                src={logo}
                alt="CREDTECH"
                className="logo"
              />
            </Link>
          </div>

          {/* Event Card with background */}
          <div className="event-card">
            <div className="event-card-content d-flex align-items-center">
              <div className="event-card-details">
                <div className="event-info-row center-box">
                  <p className="event-card-date">
                    {loading
                      ? "Loading..."
                      : error
                        ? error
                        : latestEvent
                          ? `${latestEvent.city || latestEvent.venue || "Pune"} Chapter | ${compactHeaderDate()}`
                          : "Pune Chapter | Jan 1"}
                  </p>

                  {isRegisterEnabled && (
                    <button
                      onClick={handleRegisterClick}
                      disabled={loading}
                      className="register-btn"
                    >
                      Register Here
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="d-md-none">
            <button
              className="mobile-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>

          <div className="social-icons d-none d-md-flex gap-3">
            <a
              href="https://www.facebook.com/cred.tech2020"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange"
            >
              <FaFacebookF size={32} />
            </a>
            <a
              href="https://x.com/tech_cred2020"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange"
            >
              <FaTwitter size={32} />
            </a>
            <a
              href="https://www.instagram.com/cred_tech2020/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange"
            >
              <FaInstagram size={32} />
            </a>
            <a
              href="https://www.linkedin.com/showcase/cred-tech2020/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange"
            >
              <FaLinkedinIn size={32} />
            </a>
            <a
              href="https://www.youtube.com/@cred_tech2020"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange"
            >
              <FaYoutube size={32} />
            </a>
          </div>
        </div>

        <nav className="d-none d-md-flex justify-content-center mb-4">
          <ul className="nav-list d-flex">
            {navItems.map((item) => (
              <li key={item.name} className="nav-item">
                <Link to={item.path} className="nav-link">
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {isMenuOpen && (
          <div className="mobile-menu d-md-none">
            <ul className="mobile-nav-list">
              {navItems.map((item) => (
                <li key={item.name} className="mobile-nav-item">
                  <Link
                    to={item.path}
                    className="mobile-nav-link"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mobile-social-icons d-flex justify-content-center gap-3 mt-4">
              <a
                href="https://www.facebook.com/cred.tech2020"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange"
              >
                <FaFacebookF size={24} />
              </a>
              <a
                href="https://x.com/tech_cred2020"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange"
              >
                <FaTwitter size={24} />
              </a>
              <a
                href="https://www.instagram.com/cred_tech2020/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange"
              >
                <FaInstagram size={24} />
              </a>
              <a
                href="https://www.linkedin.com/showcase/cred-tech2020/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange"
              >
                <FaLinkedinIn size={24} />
              </a>
              <a
                href="https://www.youtube.com/@cred_tech2020"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange"
              >
                <FaYoutube size={24} />
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default HeaderComponent;
